import * as yup from 'yup';
import { RELATIONSHIP_TYPES, type RelationshipType } from '@/types/clientsApiTypes';
import { emailValidation, nullableString, validatePhoneNumber } from '@/utils/validations';
import {
  FIRST_NAME_MAX_LENGTH,
  firstNameMaxMessage,
  LAST_NAME_MAX_LENGTH,
  lastNameMaxMessage,
  MIDDLE_NAME_MAX_LENGTH,
  middleNameMaxMessage,
} from '@/utils/validations/client-validations';

export const relatedBasicInfoClientSchema = yup.object({
  client_id: yup.number().optional().defined(),
  userHasCertifiedTheInformation: yup
    .boolean()
    .oneOf([true, false], 'Please certify the information to proceed')
    .optional()
    .defined()
    .default(false),
  preferred_name: nullableString().optional().defined(),
  first_name: yup.string().optional().defined().max(FIRST_NAME_MAX_LENGTH, firstNameMaxMessage),
  middle_name: nullableString().optional().defined().max(MIDDLE_NAME_MAX_LENGTH, middleNameMaxMessage),
  last_name: yup.string().optional().defined().max(LAST_NAME_MAX_LENGTH, lastNameMaxMessage),
  gender: nullableString().optional(),
  relationship: yup
    .string()
    .optional()
    .defined()
    .nullable()
    .default(null)
    .test('is-valid-relationship', 'Unsupported relationship. Please select a new one.', (value) => {
      if (!value) return true;
      return Object.values(RELATIONSHIP_TYPES).includes(value as RelationshipType);
    }),
  date_of_birth: yup.string().optional().defined().nullable(),
  contact_primary_client_id: yup.number().optional().defined().nullable().default(null),
  email: yup
    .string()
    .email('Invalid email')
    .test('is-valid-email', 'Invalid email', (value) => {
      return emailValidation(value ?? '');
    })
    .optional()
    .defined(),
  additional_emails: yup
    .array()
    .of(
      yup.object({
        email: yup
          .string()
          .email('Invalid email')
          .test('is-valid-email', 'Invalid email', (value) => {
            return emailValidation(value ?? '');
          })
          .required('Email is required'),
      })
    )
    .required()
    .default([]),
  phone: nullableString()
    .optional()
    .test('valid-phone-format', 'Invalid phone number', (value) => validatePhoneNumber(value ?? null)),
  phones: yup
    .array()
    .of(
      yup.object({
        phone: yup
          .string()
          .optional()
          .defined()
          .test(
            'valid-phone-format',
            'Invalid phone number',
            (value) => !value || value.trim() === '' || validatePhoneNumber(value)
          ),
        title: yup.string().nullable().defined(),
      })
    )
    .required()
    .default([]),
  preferred_airport_ids: yup.array().of(yup.number().nullable()).nullable().optional().default([]),
});

export const relatedBasicInfoSchema = yup.object({
  related_passengers: yup.array().of(
    yup.object({
      client: relatedBasicInfoClientSchema,
    })
  ),
});

export type RelatedBasicInfoFormValues = yup.InferType<typeof relatedBasicInfoSchema>;